import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import { Pill } from './tables/NavigatorManageTable';

const InstructionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 10px;
`;

const PickerInstruction = styled.p`
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #757575;
  font-weight: 400;
  margin: 0;
`;

const PickerPanels = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PanelBox = styled.div`
  flex: 1;
  border: 1px solid #cccccc;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PanelHeader = styled.div`
  background: ${({ color }) => color};
  color: #ffffff;
  text-align: center;
  padding: 8px 12px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 600;
`;

const PanelSearch = styled.input`
  width: 100%;
  border: none;
  border-bottom: 1px solid #eeeeee;
  padding: 8px 12px;
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #555555;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #aaaaaa;
  }
`;

const PanelList = styled.div`
  overflow-y: auto;
  height: 220px;
`;

const PanelItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  padding-left: ${({ level }) => `${12 + level * 16}px`};
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #333333;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ArrowSeparator = styled.div`
  font-size: 20px;
  color: #aaaaaa;
  flex-shrink: 0;
  padding-top: 36px;

  @media (max-width: 600px) {
    padding-top: 0;
    align-self: center;
    transform: rotate(90deg);
  }
`;

const CalendarPicker = ({
  allCalendars,
  value,
  onChange,
  leftLabel,
  rightLabel,
  searchPlaceholder,
  instructionText,
  instructionActions,
}) => {
  const [noAccessSearch, setNoAccessSearch] = useState('');
  const [hasAccessSearch, setHasAccessSearch] = useState('');

  const calendarMap = useMemo(
    () => Object.fromEntries(allCalendars.map((c) => [c.id, c])),
    [allCalendars],
  );

  const getLevel = (cal) => {
    let level = 0;
    let parentId = cal.parentCalendarId;
    while (parentId && calendarMap[parentId]) {
      level += 1;
      parentId = calendarMap[parentId].parentCalendarId;
    }
    return level;
  };

  const selectedIds = new Set((value || []).map((c) => c.id));

  const noAccessList = allCalendars
    .filter((c) => !selectedIds.has(c.id))
    .filter((c) => c.name.toLowerCase().includes(noAccessSearch.toLowerCase()));

  const hasAccessList = (value || [])
    .filter((c) => c.name.toLowerCase().includes(hasAccessSearch.toLowerCase()));

  const renderItem = (cal, onClick) => {
    const level = getLevel(cal);
    return (
      <PanelItem key={cal.id} level={level} onClick={onClick}>
        {level > 0 && (
          <Icon
            icon='sub-arrow'
            size={12}
            color='#929292'
            styledProps={{ marginBottom: '6px', flexShrink: 0 }}
          />
        )}
        {cal.name}
        {cal.archived && <Pill>Archived</Pill>}
      </PanelItem>
    );
  };

  return (
    <>
      <InstructionRow>
        <PickerInstruction>{instructionText}</PickerInstruction>
        {instructionActions}
      </InstructionRow>
      <PickerPanels>
        <PanelBox>
          <PanelHeader color='#B94A48'>{leftLabel}</PanelHeader>
          <PanelSearch
            placeholder={searchPlaceholder}
            value={noAccessSearch}
            onChange={(e) => setNoAccessSearch(e.target.value)}
          />
          <PanelList>
            {noAccessList.map((cal) => renderItem(
              cal,
              () => onChange([...(value || []), { id: cal.id, name: cal.name }]),
            ))}
          </PanelList>
        </PanelBox>
        <ArrowSeparator>
          <Icon icon='swap-alt' size={20} color='#aaaaaa' />
        </ArrowSeparator>
        <PanelBox>
          <PanelHeader color='#5CB85C'>{rightLabel}</PanelHeader>
          <PanelSearch
            placeholder={searchPlaceholder}
            value={hasAccessSearch}
            onChange={(e) => setHasAccessSearch(e.target.value)}
          />
          <PanelList>
            {hasAccessList.map((cal) => renderItem(
              cal,
              () => onChange((value || []).filter((c) => c.id !== cal.id)),
            ))}
          </PanelList>
        </PanelBox>
      </PickerPanels>
    </>
  );
};

const calendarShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  parentCalendarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  archived: PropTypes.bool,
});

CalendarPicker.propTypes = {
  allCalendars: PropTypes.arrayOf(calendarShape).isRequired,
  value: PropTypes.arrayOf(calendarShape).isRequired,
  onChange: PropTypes.func.isRequired,
  leftLabel: PropTypes.string,
  rightLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  instructionText: PropTypes.string,
  instructionActions: PropTypes.node,
};

CalendarPicker.defaultProps = {
  leftLabel: 'No Access',
  rightLabel: 'Has Access',
  searchPlaceholder: 'Search Calendars',
  instructionText: 'Click calendar names to move between access boxes',
  instructionActions: null,
};

export default CalendarPicker;
